import {
  Component,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy, ViewChild, ElementRef,
} from '@angular/core';
import {
  FormControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RequireMatch } from '@shared/validators/app.validators';

@Component({
  selector: 'app-autocomplete',
  imports: [
    TranslateModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    ReactiveFormsModule,
    CommonModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.css',
})
export class AutocompleteComponent<T extends Record<string, any>>
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, Validator {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('autoInput', { read: ElementRef }) autoInput!: ElementRef<HTMLInputElement>;

  myControl = new FormControl<T | null>(null);
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() options: T[] = [];
  @Input() displayWith!: (value: any) => string;
  @Input() fieldName: string = 'name';
  @Input() selectedValue: T | undefined = undefined;
  @Input() required: boolean = false;
  @Input() requireMatch: boolean = false;
  @Input() openSearchModal: boolean = false;
  @Output() onSelect: EventEmitter<T> = new EventEmitter<T>(undefined);

  filteredOptions: T[] = [];

  private onChange: any = () => {};
  private onTouched: any = () => {};

  private onValidatorChange: () => void = () => {};
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];

  constructor(private translate: TranslateService,) {
  }

  ngOnInit() {
    if (this.requireMatch) {
      //this.myControl.addValidators(RequireMatch());
      //this.myControl.updateValueAndValidity();
    }

    this.filteredOptions = [...this.options];

    this.subscriptions.push(
      this.myControl!.valueChanges.subscribe((value) => {
        const name = typeof value === 'string' ? value : (this.displayFn(value) ?? '');
        this.filteredOptions = this._filter(name);
        this.onChange(value);
        this.onTouched();
      }),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.myControl!.value) {
          const value = this.myControl!.value;
          const name = typeof value === 'string' ? value : (this.displayFn(value) ?? '');
          this.filteredOptions = this._filter(name);
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedValue']) {
      this.myControl!.setValue(changes['selectedValue'].currentValue);
    }
    if (changes['options']) {
      this.options = changes['options'].currentValue || [];
      this.filteredOptions = [...this.options];
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayFn(option: T | null): string {
    if (this.displayWith) {
      return this.displayWith(option);
    } else {
      if (typeof option === 'string') {
        return option ? this.translate.instant(option) : option;
      } else {
        if (!option) {
          return '';
        }

        if (this.fieldName === 'all') {
          const name = option['name'] ?? '';
          const costCenter = option['costCenter'] ?? '';
          const companyName =
            option['company']?.['denominazione'] ??
            option['company']?.['name'] ??
            option['company']?.['surname'] ??
            '';
          const description = option['description'] ?? '';

          return [name, costCenter, companyName, description]
            .filter((v) => !!v)
            .join(' - ');
        }

        const ret: string = option[this.fieldName] ?? '';
        return ret;
      }
    }
  }

  onAutoCompleteSelect(event: MatAutocompleteSelectedEvent) {
    if (typeof event.option.value === 'string' && event.option.value == 'otherInsertText') {
      this.clear();
    } else {
      this.onSelect.emit(event.option.value);
    }
  }

  private _filter(value: string): T[] {
    const filterValue = value.toLowerCase();
    return this.options.filter((option) => this.displayFn(option).toLowerCase().includes(filterValue));
  }

  clear() {
    this.myControl?.setValue(null);
    this.myControl?.markAsTouched();
    this.onSelect.emit(undefined);
  }

  onBlur() {
    const currentValue = this.myControl?.value;
    if (currentValue && this.filteredOptions && this.filteredOptions.length == 1) {
      this.myControl?.setValue(this.filteredOptions[0]);
    }
  }

  writeValue(value: any): void {
    this.myControl!.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.myControl!.disable();
    } else {
      this.myControl!.enable();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.myControl.valid ? null : { invalidAutocomplete: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.myControl.statusChanges.subscribe(() => fn());
  }

  openPanel(): void {
    // ensure we have options in the panel
    this.filteredOptions = [...(this.options ?? [])];

    // open on next tick so Angular has time to render options
    queueMicrotask(() => {
      if (this.openSearchModal) {
        this.autocompleteTrigger?.openPanel();
      }
    });
  }

  closePanel(): void {
    this.autocompleteTrigger?.closePanel();
  }
}
